import { useState, useEffect, useMemo } from 'react';
import { fetchDevicesDirectory } from '../services/dataService';
import type { DevicesDirectory, DeviceRef } from '../types';

export interface UseDeviceFilterOptions {
  initialGateways?: string[];
  initialSensors?: string[];
  initialValueTypes?: string[];
}

export interface UseDeviceFilterReturn {
  devicesDir: DevicesDirectory | null;
  loading: boolean;
  error: string | null;
  
  selectedGateways: string[];
  setSelectedGateways: (gateways: string[]) => void;
  
  selectedSensors: string[];
  setSelectedSensors: (sensors: string[]) => void;
  
  selectedValueTypes: string[];
  setSelectedValueTypes: (valueTypes: string[]) => void;
  
  gatewayOptions: { value: string; label: string }[];
  sensorOptions: { value: string; label: string }[];
  valueTypeOptions: { value: string; label: string }[];
  
  filteredGateways: DeviceRef[];
  filteredSensors: DeviceRef[];
  
  sensorValueTypeById: Map<string, string>;
  sensorValueTypeNameById: Map<string, string>;
  
  resetFilters: () => void;
}

export function useDeviceFilter(options: UseDeviceFilterOptions = {}): UseDeviceFilterReturn {
  const [devicesDir, setDevicesDir] = useState<DevicesDirectory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedGateways, setSelectedGateways] = useState<string[]>(options.initialGateways || []);
  const [selectedSensors, setSelectedSensors] = useState<string[]>(options.initialSensors || []);
  const [selectedValueTypes, setSelectedValueTypes] = useState<string[]>(options.initialValueTypes || []);

  useEffect(() => {
    let cancelled = false;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const dir = await fetchDevicesDirectory();
        
        if (!cancelled) {
          setDevicesDir(dir);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredSensors = useMemo(() => {
    if (!devicesDir) return [];
    
    let sensors = devicesDir.sensors || [];
    
    if (selectedGateways.length > 0) {
      const sensorIdsForGateways = new Set<string>();
      selectedGateways.forEach(gwId => {
        const sensorIds = devicesDir.mapping[gwId] || [];
        sensorIds.forEach(sid => sensorIdsForGateways.add(sid));
      });
      sensors = sensors.filter(s => sensorIdsForGateways.has(s.id));
    }
    
    if (selectedValueTypes.length > 0) {
      sensors = sensors.filter(s => 
        s.valueTypeName && selectedValueTypes.includes(s.valueTypeName)
      );
    }
    
    if (selectedSensors.length > 0) {
      sensors = sensors.filter(s => selectedSensors.includes(s.id));
    }
    
    return sensors;
  }, [devicesDir, selectedGateways, selectedSensors, selectedValueTypes]);

  const filteredGateways = useMemo(() => {
    if (!devicesDir) return [];
    
    let gateways = devicesDir.gateways || [];
    
    if (selectedSensors.length > 0) {
      const gatewayIdsForSensors = new Set<string>();
      selectedSensors.forEach(sensorId => {
        Object.entries(devicesDir.mapping).forEach(([gwId, sensorIds]) => {
          if (sensorIds.includes(sensorId)) {
            gatewayIdsForSensors.add(gwId);
          }
        });
      });
      gateways = gateways.filter(g => gatewayIdsForSensors.has(g.id));
    }
    
    if (selectedValueTypes.length > 0) {
      const sensorsWithValueType = (devicesDir.sensors || [])
        .filter(s => s.valueTypeName && selectedValueTypes.includes(s.valueTypeName))
        .map(s => s.id);
      
      const gatewayIdsForValueTypes = new Set<string>();
      sensorsWithValueType.forEach(sensorId => {
        Object.entries(devicesDir.mapping).forEach(([gwId, sensorIds]) => {
          if (sensorIds.includes(sensorId)) {
            gatewayIdsForValueTypes.add(gwId);
          }
        });
      });
      
      gateways = gateways.filter(g => gatewayIdsForValueTypes.has(g.id));
    }
    
    if (selectedGateways.length > 0) {
      gateways = gateways.filter(g => selectedGateways.includes(g.id));
    }
    
    return gateways;
  }, [devicesDir, selectedGateways, selectedSensors, selectedValueTypes]);

  const gatewayOptions = useMemo(() => {
    if (!devicesDir) return [];
    
    let gateways = devicesDir.gateways || [];
    
    if (selectedSensors.length > 0) {
      const gatewayIdsForSensors = new Set<string>();
      selectedSensors.forEach(sensorId => {
        Object.entries(devicesDir.mapping).forEach(([gwId, sensorIds]) => {
          if (sensorIds.includes(sensorId)) {
            gatewayIdsForSensors.add(gwId);
          }
        });
      });
      gateways = gateways.filter(g => gatewayIdsForSensors.has(g.id));
    }
    
    if (selectedValueTypes.length > 0) {
      const sensorsWithValueType = (devicesDir.sensors || [])
        .filter(s => s.valueTypeName && selectedValueTypes.includes(s.valueTypeName))
        .map(s => s.id);
      
      const gatewayIdsForValueTypes = new Set<string>();
      sensorsWithValueType.forEach(sensorId => {
        Object.entries(devicesDir.mapping).forEach(([gwId, sensorIds]) => {
          if (sensorIds.includes(sensorId)) {
            gatewayIdsForValueTypes.add(gwId);
          }
        });
      });
      
      gateways = gateways.filter(g => gatewayIdsForValueTypes.has(g.id));
    }
    
    return gateways.map(g => ({
      value: g.id,
      label: g.name
    }));
  }, [devicesDir, selectedSensors, selectedValueTypes]);

  const sensorOptions = useMemo(() => {
    if (!devicesDir) return [];
    
    let sensors = devicesDir.sensors || [];
    
    if (selectedGateways.length > 0) {
      const sensorIdsForGateways = new Set<string>();
      selectedGateways.forEach(gwId => {
        const sensorIds = devicesDir.mapping[gwId] || [];
        sensorIds.forEach(sid => sensorIdsForGateways.add(sid));
      });
      sensors = sensors.filter(s => sensorIdsForGateways.has(s.id));
    }
    
    if (selectedValueTypes.length > 0) {
      sensors = sensors.filter(s => 
        s.valueTypeName && selectedValueTypes.includes(s.valueTypeName)
      );
    }
    
    return sensors.map(s => ({
      value: s.id,
      label: s.name
    }));
  }, [devicesDir, selectedGateways, selectedValueTypes]);

  const valueTypeOptions = useMemo(() => {
    if (!devicesDir) return [];
    
    let sensors = devicesDir.sensors || [];
    
    if (selectedGateways.length > 0) {
      const sensorIdsForGateways = new Set<string>();
      selectedGateways.forEach(gwId => {
        const sensorIds = devicesDir.mapping[gwId] || [];
        sensorIds.forEach(sid => sensorIdsForGateways.add(sid));
      });
      sensors = sensors.filter(s => sensorIdsForGateways.has(s.id));
    }
    
    if (selectedSensors.length > 0) {
      sensors = sensors.filter(s => selectedSensors.includes(s.id));
    }
    
    const uniqueValueTypes = new Set<string>();
    sensors.forEach(s => {
      if (s.valueTypeName) {
        uniqueValueTypes.add(s.valueTypeName);
      }
    });
    
    return Array.from(uniqueValueTypes)
      .sort()
      .map(vt => ({
        value: vt,
        label: vt
      }));
  }, [devicesDir, selectedGateways, selectedSensors]);

  // Create mapping of sensor ID to valueTypeValue
  const sensorValueTypeById = useMemo(() => {
    const map = new Map<string, string>();
    if (!devicesDir) return map;
    
    devicesDir.sensors.forEach(sensor => {
      if (sensor.valueTypeValue) {
        map.set(sensor.id, sensor.valueTypeValue);
      }
    });
    
    return map;
  }, [devicesDir]);

  // Create mapping of sensor ID to valueTypeName
  const sensorValueTypeNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!devicesDir) return map;
    
    devicesDir.sensors.forEach(sensor => {
      if (sensor.valueTypeName) {
        map.set(sensor.id, sensor.valueTypeName);
      }
    });
    
    return map;
  }, [devicesDir]);

  const resetFilters = () => {
    setSelectedGateways([]);
    setSelectedSensors([]);
    setSelectedValueTypes([]);
  };

  return {
    devicesDir,
    loading,
    error,
    selectedGateways,
    setSelectedGateways,
    selectedSensors,
    setSelectedSensors,
    selectedValueTypes,
    setSelectedValueTypes,
    gatewayOptions,
    sensorOptions,
    valueTypeOptions,
    filteredGateways,
    filteredSensors,
    sensorValueTypeById,
    sensorValueTypeNameById,
    resetFilters,
  };
}
